import { useLingui } from '@lingui/react/macro';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import { Panel, Separator, Group as PanelGroup, useDefaultLayout } from 'react-resizable-panels';
import { useThreadPanelStore } from '../stores';
import { useDismissibleLayer } from '../hooks/useDismiss';
import { MAX_THREAD_PANEL_WIDTH, MIN_THREAD_PANEL_WIDTH } from '../lib/threadPanelPlacement';
import styles from './ShellLayout.module.css';

/**
 * `react-resizable-panels` persists drag sizes under
 * `react-resizable-panels:<layout id>`, holding a record of panel id to size,
 * which makes both strings user-visible state: changing the layout id orphans
 * every layout every user has saved, and renaming a panel drops that panel's
 * saved size. The id carries a version deliberately — when a panel starts
 * measuring something else, bumping it discards the saved sizes rather than
 * silently reinterpreting them at the new meaning. The thread is not among the
 * panels: it is placed by the shell itself and owns its own width, so the group
 * is the sidebar and the channel and nothing else.
 */
const SHELL_LAYOUT_ID = 'borgee-layout-v3';
const SHELL_PANEL_IDS = ['sidebar', 'main'] as const;

const [SIDEBAR_PANEL_ID, MAIN_PANEL_ID] = SHELL_PANEL_IDS;

const DRAWER_ID = 'shell-rail-drawer';
const THREAD_COLUMN_ID = 'shell-thread-column';

/**
 * The widths the sidebar and the channel refuse to shrink past. A thread sits
 * beside their group rather than inside it, so the group's solver has no say
 * over it and cannot claw width back when the viewport narrows; the shell works
 * out what the thread may take and publishes that to CSS instead. Collapsing
 * the sidebar unmounts its panel and the separator beside it, so both drop out
 * of that sum and the thread may have the room they were holding.
 */
const SIDEBAR_MIN_WIDTH = 172;
const MAIN_MIN_WIDTH = 360;

const groupMinWidth = (sidebarCollapsed: boolean) =>
  sidebarCollapsed
    ? `${MAIN_MIN_WIDTH}px`
    : `calc(${SIDEBAR_MIN_WIDTH + MAIN_MIN_WIDTH}px + var(--shell-separator-width))`;

/**
 * The thread column's ceiling at the current viewport, and the same one in both
 * placements: whatever the shell has left over the navigation strip and the
 * group's own minimums, never below the thread's own minimum. Docked, it is
 * what makes the thread yield first on a narrowing window rather than starving
 * the two columns beside it. Floating, it is what keeps the thread on the
 * channel's right edge — a stored width the window cannot spare would otherwise
 * cover the channel outright and then the sidebar with it, leaving the gesture
 * that puts a floating thread away almost nothing to land on.
 */
const threadColumnMaxWidth = (sidebarCollapsed: boolean) =>
  `max(${MIN_THREAD_PANEL_WIDTH}px, 100% - var(--primary-navigation-rail-width) - ${groupMinWidth(sidebarCollapsed)})`;

const THREAD_RESIZE_KEY_STEP = 16;

/** Held on the document while the thread's edge is being dragged. */
const RESIZING_CURSOR_CLASS = 'thread-resizing';

/**
 * The width one key press asks for, or `null` for a key the handle ignores.
 * Left and right move the edge, and up and down are taken as their synonyms so
 * a member who reaches for the other axis still gets a resize. Home and End go
 * to the bounds this handle publishes through `aria-valuemin` / `aria-valuemax`,
 * and all three values describe the thread: Home is therefore the narrowest
 * thread, which walks the edge right rather than left, because the panel this
 * handle speaks for sits after it instead of before it.
 */
const threadWidthForKey = (key: string, width: number): number | null => {
  switch (key) {
    case 'ArrowLeft':
    case 'ArrowUp':
      return width + THREAD_RESIZE_KEY_STEP;
    case 'ArrowRight':
    case 'ArrowDown':
      return width - THREAD_RESIZE_KEY_STEP;
    case 'Home':
      return MIN_THREAD_PANEL_WIDTH;
    case 'End':
      return MAX_THREAD_PANEL_WIDTH;
    default:
      return null;
  }
};

interface Props {
  isMobile: boolean;
  /**
   * Fixed-width navigation strip (PrimaryNavigationRail). It frames the resizable columns
   * rather than being one of them, so it survives a sidebar collapse.
   */
  primaryNavigation: React.ReactNode;
  /** Route-specific sidebar and user panel — the column the collapse toggle hides. */
  /** Null removes the entire secondary-navigation column for this route. */
  sectionSidebar: React.ReactNode | null;
  main: React.ReactNode;
  /** `null` unless a thread is open, which is what makes the third surface appear. */
  thread: React.ReactNode | null;
  /** The thread leaving without its own close control being pressed. */
  onThreadDismiss: () => void;
  sidebarCollapsed: boolean;
  onSidebarCollapsedToggle: () => void;
  drawerOpen: boolean;
  onDrawerToggle: () => void;
  onDrawerClose: () => void;
}

/**
 * Drags the thread column's left edge. The column is anchored to the right in
 * both placements, so the width grows as the pointer travels left and the
 * delta is subtracted rather than added.
 *
 * `width` is the width the column is drawn at, not the width the store holds —
 * the two part ways whenever the cap bites — so a gesture starts from the edge
 * the member is looking at and `aria-valuenow` announces that edge.
 *
 * A gesture is announced start to finish rather than reported as a width: the
 * width moves on every pointer move, and what becomes of it once the edge comes
 * to rest is the shell's to decide, including for a gesture that moved the edge
 * nowhere. One key press is a whole gesture in itself. Two of the group
 * separator's keys have no counterpart here and are left alone: Enter toggles a
 * collapsible panel, and the thread has no collapsed state — closing it is the
 * header's job; F6 cycles focus around the separators of one group, and this
 * handle belongs to no group.
 */
function ThreadResizer({
  width,
  onResizeStart,
  onResize,
  onResizeEnd,
}: {
  width: number;
  onResizeStart: () => void;
  onResize: (width: number) => void;
  onResizeEnd: () => void;
}) {
  const { t, i18n } = useLingui();
  const drag = useRef<{
    pointerId: number;
    originX: number;
    originWidth: number;
  } | null>(null);
  // The pressed look follows this flag rather than `:active`, which Gecko drops
  // for a pointerdown whose default action is prevented, as this one's is:
  // https://bugzilla.mozilla.org/show_bug.cgi?id=1293741
  const [dragging, setDragging] = useState(false);

  // The pointer is captured for the drag, but the cursor is still resolved
  // against the element under it, so the resize cursor is held on the document
  // for as long as the drag lasts rather than left to whatever is swept over.
  useEffect(() => {
    if (!dragging) return;
    document.body.classList.add(RESIZING_CURSOR_CLASS);
    return () => document.body.classList.remove(RESIZING_CURSOR_CLASS);
  }, [dragging]);

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) return;
      drag.current = {
        pointerId: event.pointerId,
        originX: event.clientX,
        originWidth: width,
      };
      setDragging(true);
      onResizeStart();
      event.currentTarget.setPointerCapture(event.pointerId);
      // Without the preventDefault the drag selects the text it sweeps over, but
      // it also suppresses the mousedown that would have focused the handle, so
      // the focus is taken explicitly and the arrow keys work the moment the
      // pointer is released.
      event.currentTarget.focus();
      event.preventDefault();
    },
    [width, onResizeStart],
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const active = drag.current;
      if (active === null || active.pointerId !== event.pointerId) return;
      onResize(active.originWidth - (event.clientX - active.originX));
    },
    [onResize],
  );

  const handlePointerEnd = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const active = drag.current;
      if (active?.pointerId !== event.pointerId) return;
      drag.current = null;
      setDragging(false);
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      onResizeEnd();
    },
    [onResizeEnd],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const next = threadWidthForKey(event.key, width);
      if (next === null) return;
      event.preventDefault();
      onResizeStart();
      // A gesture comes to rest on the width the column ends up drawn at, so the
      // step has to reach the screen before `onResizeEnd` reads it back. A pointer
      // move is already on screen by the time the release arrives; a key press
      // would still be queued behind the handler that fired it.
      flushSync(() => onResize(next));
      onResizeEnd();
    },
    [onResizeStart, onResize, onResizeEnd, width],
  );

  return (
    <div
      className={styles.threadResizer}
      data-testid="shell-thread-resizer"
      data-dragging={dragging || undefined}
      role="separator"
      aria-orientation="vertical"
      aria-label={t`Resize thread`}
      aria-controls={THREAD_COLUMN_ID}
      aria-valuenow={width}
      aria-valuemin={MIN_THREAD_PANEL_WIDTH}
      aria-valuemax={MAX_THREAD_PANEL_WIDTH}
      tabIndex={0}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
      onKeyDown={handleKeyDown}
    />
  );
}

/**
 * The width an element is drawn at, kept in step with the box itself.
 *
 * The thread column's drawn width is not the width the shell asked for: pinned,
 * CSS caps the column against the room the sidebar and the channel refuse to
 * give up, so on a narrow window the store keeps the width the member chose
 * while the screen shows less of it. A resize that started from the stored
 * number would spend the difference moving an edge nobody can see, and would
 * announce a width that is not on screen, so the handle reads the box instead.
 *
 * Every render can change the box — a new width, a placement toggle — hence the
 * measurement after each one; the observer catches the changes no render
 * caused, the window's above all. What comes back feeds an attribute and a
 * gesture, never a layout rule, so measuring the box cannot resize it.
 */
function useRenderedWidth() {
  const [element, setElement] = useState<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);

  /** Reads the box now, for a caller that cannot wait for the next render. */
  const measure = useCallback(() => {
    if (element === null) return null;
    const drawn = Math.round(element.getBoundingClientRect().width);
    setWidth(drawn);
    return drawn;
  }, [element]);

  useLayoutEffect(() => {
    measure();
  });

  useLayoutEffect(() => {
    if (element === null) return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [element, measure]);

  return { ref: setElement, element, width, measure };
}

/**
 * Owns how the shell is laid out at the current viewport: where the four
 * surfaces — nav, sidebar, main, thread — go, the chrome that placement needs
 * (the drawer toggle and the sidebar collapse toggle), and the persisted column
 * sizes. Wide viewports dock the nav strip beside resizable columns; mobile
 * puts nav and sidebar together in a drawer and gives the thread a surface
 * stacked over main, because a phone has no room to show two of them side by
 * side. Both branches receive the very same nodes, so a surface can never exist
 * in one layout and be missing from the other.
 *
 * Collapsing unmounts the sidebar column instead of shrinking it to nothing: a
 * zero-width column would keep its channel list in the tab order and in the
 * accessibility tree while the member has asked for it to be gone.
 * `sidebarCollapsed` is a wide-viewport preference only — the mobile drawer is
 * opened to reach the sidebar, so honoring it there would open an empty drawer.
 *
 * At wide viewports the thread is placed beside the resizable group rather than
 * inside it, because its two placements — docked next to the channel, or
 * floating over the channel's right edge — differ only in this one wrapper's
 * CSS. Toggling between them therefore keeps the thread mounted, and an
 * in-progress reply survives the toggle.
 */
export default function ShellLayout({
  isMobile,
  primaryNavigation,
  sectionSidebar,
  main,
  thread,
  onThreadDismiss,
  sidebarCollapsed,
  onSidebarCollapsedToggle,
  drawerOpen,
  onDrawerToggle,
  onDrawerClose,
}: Props) {
  const { t, i18n } = useLingui();
  const hasSectionSidebar = sectionSidebar !== null;
  const { defaultLayout, onLayoutChanged } = useDefaultLayout({
    id: SHELL_LAYOUT_ID,
    onlySaveAfterUserInteractions: true,
  });
  const threadMode = useThreadPanelStore((s) => s.mode);
  const threadWidth = useThreadPanelStore((s) => s.sessionLayout?.width ?? s.width);
  const workspaceExpanded = useThreadPanelStore((s) => s.sessionLayout?.expanded === true) && thread !== null && !isMobile;
  const [workspaceLeft, setWorkspaceLeft] = useState(0);
  const setThreadWidth = useThreadPanelStore((s) => s.setWidth);
  const persistThreadWidth = useThreadPanelStore((s) => s.persistWidth);
  const shellRef = useRef<HTMLDivElement>(null);
  const mainSurfaceRef = useRef<HTMLDivElement>(null);
  const {
    ref: threadColumnRef,
    element: threadColumn,
    width: renderedThreadWidth,
    measure: measureThreadColumn,
  } = useRenderedWidth();

  useLayoutEffect(() => {
    if (isMobile || !thread) return;
    const shell = shellRef.current;
    const mainSurface = mainSurfaceRef.current;
    if (!shell || !mainSurface) return;
    const measure = () => setWorkspaceLeft(mainSurface.getBoundingClientRect().left - shell.getBoundingClientRect().left);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(shell);
    observer.observe(mainSurface);
    return () => observer.disconnect();
  }, [isMobile, thread, sidebarCollapsed, workspaceExpanded]);

  /**
   * The two widths a resize gesture started from: the one the store held, and
   * the one the column was drawn at. Under the cap they are different numbers,
   * and both are needed to tell what the gesture came to.
   */
  const resizeOrigin = useRef<{ asked: number; drawn: number } | null>(null);

  const beginThreadResize = useCallback(() => {
    const drawn = measureThreadColumn();
    resizeOrigin.current = drawn === null ? null : { asked: threadWidth, drawn };
  }, [measureThreadColumn, threadWidth]);

  /**
   * Ends a resize on the width the column was drawn at rather than the width it
   * was asked for. The two part company under the cap, where a handle dragged
   * past what the shell can spare keeps travelling while the edge stands still;
   * storing the request would keep a width the member was never shown.
   *
   * A gesture that leaves the edge where it found it asked for nothing at all,
   * and is put back rather than settled: a press that never travelled, a drag
   * or a key press the cap had no room to honour, a jiggle that came back to
   * the pixel it started on. Settling one of those would hand storage whatever
   * this window happens to allow today and lose the width the member chose on
   * the one they set it from. Nothing but a gesture reaches here, so a window
   * that merely cannot draw the stored width leaves it alone either way.
   */
  const settleThreadWidth = useCallback(() => {
    const origin = resizeOrigin.current;
    resizeOrigin.current = null;
    const drawn = measureThreadColumn();
    if (origin === null || drawn === null) return;
    if (drawn === origin.drawn) {
      setThreadWidth(origin.asked);
      return;
    }
    setThreadWidth(drawn);
    persistThreadWidth();
  }, [measureThreadColumn, setThreadWidth, persistThreadWidth]);

  /**
   * Whether a press reaches what the thread is drawn over.
   *
   * The layers open over the app answer for themselves — every one of them is
   * on the dismissible-layer stack above the thread, which registers when it
   * opens, so a press one of them caught never reaches here. What is left is
   * the app, and a floating thread is drawn over one part of it: the channel,
   * on whose right edge it sits. A press there is the member reaching for what
   * the thread is covering. The navigation strip, the sidebar and the splitter
   * between the two are beside the thread rather than under it, and a press on
   * one of them is a member going somewhere — reverting it and returning them
   * to the channel they came from would undo a click they meant.
   */
  const isPressBehindThread = useCallback((target: Element) => {
    const mainSurface = mainSurfaceRef.current;
    return mainSurface !== null && mainSurface.contains(target);
  }, []);

  /**
   * Whether an Escape is the thread's to take. The key names no surface, so it
   * reaches the thread from anywhere in the app — including with nothing
   * focused at all, where it lands on the document body, which the shell is
   * inside rather than the other way round.
   *
   * What it must not do is answer for a surface the shell never put up. antd
   * portals every dialog and popup to `document.body`, keeps the keyboard
   * inside a dialog while it is open, and answers Escape from a global stack of
   * its own that the dismissible-layer registry cannot join; a key pressed in
   * there closes that dialog, and taking it here as well would dismiss the
   * thread out from under a member who was only closing a dialog.
   */
  const isEscapeForThread = useCallback((target: Element) => {
    const shell = shellRef.current;
    return shell !== null && (shell.contains(target) || target.contains(shell));
  }, []);

  /**
   * A floating thread is drawn over the channel's right edge, so a press on the
   * channel is the member reaching for what the thread is sitting on, and it
   * puts the thread away. Docked, the thread covers nothing — it is a column of
   * the shell like the sidebar — and only its close control dismisses it.
   * Mobile has no "outside" to press: the thread owns the screen.
   *
   * Escape names no surface, so it dismisses in either placement — but only
   * while the thread is the topmost layer, since anything opened inside it
   * opened later and the key is that layer's.
   *
   * Both leave the composer's text where it is: it is kept per surface, so a
   * dismissal costs a member no reply they were part-way through.
   */
  const threadIsOpen = thread !== null;
  useDismissibleLayer({
    active: threadIsOpen && !isMobile,
    within: threadColumn,
    onEscape: onThreadDismiss,
    claimsEscapeOn: isEscapeForThread,
    onOutsidePress: threadMode === 'float' && !workspaceExpanded ? onThreadDismiss : undefined,
    dismissesOn: isPressBehindThread,
  });

  if (isMobile) {
    return (
      <>
        <button
          type="button"
          className={styles.hamburgerButton}
          data-testid="shell-navigation-toggle"
          onClick={onDrawerToggle}
          aria-label={t`Navigation`}
          aria-controls={DRAWER_ID}
          aria-expanded={drawerOpen}
        >
          ☰
        </button>
        {drawerOpen && (
          <div
            className={styles.railDrawerOverlay}
            data-testid="shell-drawer-overlay"
            onClick={onDrawerClose}
          />
        )}
        <div
          id={DRAWER_ID}
          className={`${styles.railDrawer}${drawerOpen ? ` ${styles.railDrawerOpen}` : ''}`}
          data-testid="shell-drawer"
          data-state={drawerOpen ? 'open' : 'closed'}
        >
          <div className={styles.navRail} data-testid="shell-primary-navigation">
            {primaryNavigation}
          </div>
          {sectionSidebar}
        </div>
        <div className="main-content" data-testid="shell-main-surface">
          {main}
        </div>
        {thread && (
          <div className={styles.threadSurface} data-testid="shell-thread-surface">
            {thread}
          </div>
        )}
      </>
    );
  }

  return (
    <div
      className={styles.shellColumns}
      data-testid="shell-layout"
      ref={shellRef}
      style={
        {
          '--thread-column-max-width': threadColumnMaxWidth(sidebarCollapsed),
        } as React.CSSProperties
      }
    >
      <div className={styles.navRail} data-testid="shell-primary-navigation">
        {primaryNavigation}
        {hasSectionSidebar && (
          <button
            type="button"
            className={styles.navRailToggle}
            data-testid="shell-sidebar-toggle"
            onClick={onSidebarCollapsedToggle}
            aria-label={sidebarCollapsed ? t`Expand sidebar` : t`Collapse sidebar`}
            aria-expanded={!sidebarCollapsed}
            title={sidebarCollapsed ? t`Expand sidebar` : t`Collapse sidebar`}
          >
            {sidebarCollapsed ? <MenuUnfoldOutlined aria-hidden="true" /> : <MenuFoldOutlined aria-hidden="true" />}
          </button>
        )}
      </div>
      <PanelGroup
        orientation="horizontal"
        id={SHELL_LAYOUT_ID}
        defaultLayout={defaultLayout}
        onLayoutChanged={onLayoutChanged}
      >
        {hasSectionSidebar && !sidebarCollapsed && (
          <>
            <Panel
              id={SIDEBAR_PANEL_ID}
              defaultSize="200px"
              minSize={`${SIDEBAR_MIN_WIDTH}px`}
              maxSize="272px"
              className={styles.panelLeft}
            >
              <div data-testid="shell-sidebar-panel">{sectionSidebar}</div>
            </Panel>
            <Separator />
          </>
        )}
        <Panel id={MAIN_PANEL_ID} minSize={`${MAIN_MIN_WIDTH}px`} className={styles.panelMain}>
          <div className="main-content" data-testid="shell-main-surface" ref={mainSurfaceRef} {...(workspaceExpanded ? { inert: '' } : {})} aria-hidden={workspaceExpanded || undefined}>
            {main}
          </div>
        </Panel>
      </PanelGroup>
      {thread && (
        <div
          id={THREAD_COLUMN_ID}
          ref={threadColumnRef}
          className={`${styles.threadColumn}${threadMode === 'float' ? ` ${styles.threadColumnFloat}` : ''}${workspaceExpanded ? ` ${styles.threadColumnExpanded}` : ''}`}
          data-testid="shell-thread-column"
          data-mode={threadMode}
          data-expanded={workspaceExpanded}
          style={workspaceExpanded ? { left: workspaceLeft } : { width: `${threadWidth}px` }}
        >
          {!workspaceExpanded && <ThreadResizer
            width={renderedThreadWidth}
            onResizeStart={beginThreadResize}
            onResize={setThreadWidth}
            onResizeEnd={settleThreadWidth}
          />}
          {thread}
        </div>
      )}
      {workspaceExpanded && threadMode === 'pinned' && (
        <div aria-hidden="true" className={styles.threadColumnPlaceholder} style={{ width: threadWidth }} />
      )}
    </div>
  );
}
